import { useEffect, useMemo, useRef, useState } from 'react';
import taxService from '@/services/billing/taxService';
import expenseService from '@/services/billing/expenses/expenseService';
import matterService from '@/services/matter/matterService';
import userService from '@/services/setting/userService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon, DefaultTooltip } from '@/components';
import { Can } from '@casl/react';
import { ItemsModal } from '@/pages/billing';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { Link, useNavigate } from 'react-router-dom';
import { renderMultiAvatars, FiltersToFormData, getAccessToken, decodeHtmlEntities } from '@/utils'
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal, useContactLookup } from '@/hooks';
import { SelectInput, DateInput, STATUS_INVOICE_OPTIONS } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import InvoiceTemplateService from '@/services/billing/invoice/invoiceTemplateService';
import { goTo403 } from "@/utils/Navigation";
import { ColumnToggleDropdown } from '@/components/TableFilter/ColumnToggleDropdown';
import { useUserOptions } from '@/hooks'
import { Container } from '@/components/container';

DataTable.use(DT);
const InvoiceTemplate = () => {
  const { logout } = useAuthContext();
  const fetchContactLookup = useContactLookup();
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false)
  const [contactTypeData, setContactTypeData] = useState([])
  const [taxesData, setTaxesData] = useState([])
  const [itemId, setItemId] = useState(null)
  const { ability } = useAbility();
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({
    billed_by: null,
    status: null,
    contact: null,
    matter: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const formikRef = useRef();
  const canDeleteTemplate = ability.can('delete', 'invoice');
  const canEditTemplate = ability.can('handle', 'invoice');
  const Navigate = useNavigate()
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });


  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const badgeClasses = {
    billable: 'badge-success',
    billed: 'badge-info',
    paid: 'badge-primary',
    not_billable: 'badge-danger',
  }

  const columns = [
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    {
      data: 'name',
      title: 'Template Name',
      name: 'name',
    },
    {
      data: 'is_default',
      title: 'Default Template',
      name: 'is_default',
      render: function (value) {
        const is_default = value === 1 ? 'Yes' : 'No';
        return `<span class="capitalize">${is_default}</span>`;
      }
    },
    { data: 'created_at', title: 'Created At', name: 'created_at' },

    // ---- Extended columns for missing fields ----
    // {
    //   data: 'id',
    //   title: 'ID',
    //   name: 'id',
    //   visible: false, // hidden but available for reference
    //   render: (data) => data ?? '-',
    // },
    {
      data: 'updated_at',
      title: 'Updated At',
      name: 'updated_at',
      render: (data) => data ?? '-',
    },
    // {
    //   data: 'deleted_at',
    //   title: 'Deleted At',
    //   name: 'deleted_at',
    //   render: (data) => data ?? '-',
    // },
    {
      data: 'settings',
      title: 'Settings',
      name: 'settings',
      orderable: true,
      render: function (data) {
        if (!data || typeof data !== 'object') return '-';
        const settingName =
          data?.settings?.invoice_theme?.theme ||
          data?.settings?.invoice?.title_label ||
          '—';
        return `<span class="text-truncate">${settingName}</span>`;
      },
    },
    {
      title: 'Actions',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      },
      name: 'actions',
    },
  ];

  const defaultVisibleColumns = [
    // 'serial',
    'name',
    'is_default',
    'created_at',
    'tags',
    'status',
    'is_2fa_enabled',
    'created_at',
    'actions',
  ];


  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? expenseService.updateExpense : expenseService.createExpense;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { data, status } = await action(...args);
      if (status) {
        setOpen(false);
        refresh();
        toast.success(`Expense ${isEdit ? 'Updated' : 'Created'} Successfully`, {
          description: `Expense ${isEdit ? 'updated' : 'created'} successfully.`,

        })
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const validationErrors = error.errors
        const formattedErrors = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
        );

        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',

        });
      }
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await InvoiceTemplateService.deleteInvoiceTemplate(actionId);
      if (status) {
        refresh()
        toast.success(`Invoice Template Deleted Successfully`, {
          description: `Invoice Template has been Deleted successfully.`,
        })
      }
    } catch (error) {
      console.error('Error', error)
      if (error.status === 400) {
        toast.warning("Deletion Not Allowed", {
          description:
            "This Templates is already assigned to a contact cannot be deleted.",
        });
        return
      }
      toast.error('Failed to delete Invoice Template.', {
        description: 'An error occurred while deleting template. Please try again later.',
      });
    }
  }

  const handleEdit = (id) => {
    Navigate(`/billing/invoice/template/edit/${id}/`)
  }

  const handleClone = (id) => {
    Navigate(`/billing/expense/create/${id}/`)
  }

  const handleModalClose = () => {
    setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const renderCustomActions = (row) => {
    const { id } = row;

    return (
      <DropdownMenu>
        {/* {(canDeleteExpense || canEditExpense) && (
        )} */}
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="text-gray-600">
            <KeenIcon icon="dots-vertical" className="text-[18px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <Can I="handle" a="invoice" ability={ability}>
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          </Can>
          {/* <Can I="edit" a="expense" ability={ability}> */}
          {/* <DropdownMenuItem onClick={() => handleClone(id)}>
              <KeenIcon icon="note-2" className="mr-0.5 h-4 w-4 cursor-pointer" /> Clone
            </DropdownMenuItem> */}
          {/* </Can> */}
          <Can I="handle" a="invoice" ability={ability}>
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const ajaxConfig = createDataTableConfig('/setup/templates/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[2, 'desc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }
        const contactContainer = document.getElementById(`contact-cell-${row.id}`);
        if (contactContainer) {
          if (!contactContainer._reactRoot) {
            contactContainer._reactRoot = createRoot(contactContainer);
          }
          contactContainer._reactRoot.render(renderContactName(row));
        }
        const matterContainer = document.getElementById(`matter-cell-${row.id}`);
        if (matterContainer) {
          if (!matterContainer._reactRoot) {
            matterContainer._reactRoot = createRoot(matterContainer);
          }
          matterContainer._reactRoot.render(renderMatterName(row));
        }
        const billedContainer = document.getElementById(`billed-by-${row.id}`);
        if (billedContainer) {
          if (!billedContainer._reactRoot) {
            billedContainer._reactRoot = createRoot(billedContainer);
          }
          billedContainer._reactRoot.render(renderMultiAvatars(row.billed_by));
        }
      });
    },
  });

  const resetFilters = () => {
    const cleared = {
      assigned_to: null,
      status: null,
      created_at: null,
      tags: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      //console.log('updated',updated)
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const fetchLookup = async (type) => {
    switch (type) {
      case 'contact':
        return await fetchContactLookup();
      case 'matter':
        const matters = await matterService.getAllMatters();
        return matters.data.map((matter) => ({ label: matter.name, value: matter.id }));

      default:
        return [];
    }
  };

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const renderContactName = (row) => {
    console.log('asd', row)
    if (!row.contact) {
      return <span>-</span>;
    }
    const fullName = row.contact.first_name + ' ' + row.contact.last_name;
    const { id } = row.contact;
    return (
      <span onClick={() => { handleNavigateConatct(id) }} className='cursor-pointer link'>{fullName}</span>
    )
  }

  const handleNavigateConatct = (id) => {
    Navigate(`/contacts/${id}/`)
  }

  const handleNavigateMatter = (id) => {
    Navigate(`/matters/detail/${id}/`)
  }

  const renderMatterName = (row) => {
    const { matter } = row;
    const fullName = decodeHtmlEntities(matter?.name || '');

    return (
      <span
        onClick={() => handleNavigateMatter(matter?.id || '')}
        className="cursor-pointer link"
      >
        {fullName}
      </span>
    );
  }

  return (
    <>
      <Container>
        <div className="min-w-full mb-4 card">
          <div className="card-header">
            <div className='flex items-center gap-1'>
              <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
              {/* <h3 className="card-title">Invoice Templates</h3> */}
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              {/* <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
              <KeenIcon icon="filter" className="w-fit btn-icon" />
              {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
            </button> */}
              <Can I="handle" a="invoice" ability={ability}>
                <Link to='/billing/invoice/template/create' className='text-xs btn btn-sm btn-light'>
                  <KeenIcon icon="plus" className="w-fit btn-icon" />
                  Add New
                </Link>
              </Can>
            </div>
          </div>


          <DataTable
            id="cinvoice-template-table"
            ajax={ajaxConfig}
            columns={columns}
            options={options}
            ref={tableRef}
          />

          <ItemsModal
            open={open}
            onCloseModal={handleModalClose}
            handleMode={mode}
            data={selectedData}
            onSubmit={handleSubmit}
            createFieldLoading={isLoading}
            updateFieldLoading={loading}
            taxesData={taxesData}
            ref={formikRef}
          />

          <ConfirmationModal
            show={isOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            message="Are you sure you want to delete this Invoice Template? This action is permanent and cannot be undone."
            title="Are You Sure?"
          />
        </div>
      </Container>
    </>
  )
}

export { InvoiceTemplate }
